import { PhoneField, TextField } from '@wheely/ui-kit';
import { IDENTIFIER_TYPES, type IdentifierType } from '@wheely/web-auth-lib/core';
import { IDENTIFIER_UPDATE_PHASES, useIdentifierUpdateFlow } from '@wheely/web-auth-lib/react';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { PHONE_CODES } from '@/shared/const/phone-codes';
import { getAuthErrorText } from '@/shared/utils/getAuthErrorText';
import { getCodeAndNumberFromPhone, normalizePhoneNumber } from '@/shared/utils/phoneUtils';
import { ChangeButton } from '@/shared/view/ChangeButton';
import { CodeForm } from '@/shared/view/CodeForm';
import { LinkButton } from '@/shared/view/LinkButton';
import { Button } from '@src-common/view/adapters/Button';

import s from './style.module.css';

type Props = {
  identifierType: IdentifierType;
  /** The value the profile holds today; shown locked while nothing changes. */
  currentValue: string;
  /** Another identifier is being changed: one flow at a time. */
  locked?: boolean;
  onActiveChange?: (type: IdentifierType, active: boolean) => void;
  /** The code was accepted upstream: the identifier already holds the value. */
  onUpdated: (type: IdentifierType, value: string) => void;
};

/**
 * One profile identifier: the locked current value with a Change action, then
 * the new value entry and the code confirmation, all on the identifier-update
 * flow of the v3 session. The local view never runs ahead of the flow: it
 * leaves the editing state only once the controller reports the matching
 * phase, so a dropped or failed cancel cannot desync the two.
 */
const IdentifierSection: React.VFC<Props> = ({
  identifierType,
  currentValue,
  locked,
  onActiveChange,
  onUpdated,
}) => {
  const intl = useIntl();
  const isEmail = identifierType === IDENTIFIER_TYPES.EMAIL;

  const flow = useIdentifierUpdateFlow();
  const { phase, error, transports, attemptsLeft, resendAvailableIn } = flow;
  const { start, send, submitCode, resend, cancel, reset } = flow;

  const [isEditing, setIsEditing] = useState(false);
  /** A close was requested; it lands when the flow comes back to idle. */
  const [isClosing, setIsClosing] = useState(false);
  /** A command is in flight: the buttons wait for it. */
  const [busy, setBusy] = useState(false);
  const [value, setValue] = useState('');
  const [phoneCode, setPhoneCode] = useState('+');
  const [validationError, setValidationError] = useState<string | undefined>(undefined);
  /** What start() sent last; DONE reports it up as the new current value. */
  const pendingValueRef = useRef('');

  useEffect(() => {
    onActiveChange?.(identifierType, isEditing);
  }, [identifierType, isEditing, onActiveChange]);

  const leaveEditing = useCallback(() => {
    setIsEditing(false);
    setIsClosing(false);
    setValue('');
    setValidationError(undefined);
  }, []);

  // The accepted code ends the flow, wherever the local view happens to be.
  useEffect(() => {
    if (phase === IDENTIFIER_UPDATE_PHASES.DONE) {
      reset();
      leaveEditing();
      onUpdated(identifierType, pendingValueRef.current);
    }
  }, [identifierType, leaveEditing, onUpdated, phase, reset]);

  // The requested close lands only when the cancel actually reset the flow.
  useEffect(() => {
    if (isClosing && phase === IDENTIFIER_UPDATE_PHASES.IDLE) {
      leaveEditing();
    }
  }, [isClosing, leaveEditing, phase]);

  // A failed cancel keeps the code step on screen, with its error.
  useEffect(() => {
    if (isClosing && error) {
      setIsClosing(false);
    }
  }, [error, isClosing]);

  const run = useCallback(async (command: () => Promise<void>) => {
    setBusy(true);

    try {
      await command();
    } finally {
      setBusy(false);
    }
  }, []);

  const beginEditing = useCallback(() => {
    // A leftover pending change (a close that never landed) starts over.
    if (phase !== IDENTIFIER_UPDATE_PHASES.IDLE) {
      reset();
    }

    setPhoneCode(getCodeAndNumberFromPhone(currentValue).code);
    setValue('');
    setValidationError(undefined);
    setIsClosing(false);
    setIsEditing(true);
  }, [currentValue, phase, reset]);

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      let identifier = value;

      if (!isEmail) {
        const phone = normalizePhoneNumber(phoneCode + value);

        if (!phone) {
          setValidationError(
            intl.formatMessage({
              defaultMessage: 'Please enter a valid phone number.',
              id: 'profile_error_invalid_phone',
              description: 'Phone validation error',
            }),
          );

          return;
        }

        identifier = phone;
      }

      setValidationError(undefined);
      pendingValueRef.current = identifier;
      void run(() => start({ identifierType, value: identifier }));
    },
    [identifierType, intl, isEmail, phoneCode, run, start, value],
  );

  // Cancels the pending change and reopens the entry with the value kept.
  const handleEditValue = useCallback(() => {
    void run(cancel);
  }, [cancel, run]);

  const handleCancel = useCallback(() => {
    if (phase === IDENTIFIER_UPDATE_PHASES.IDLE) {
      reset();
      leaveEditing();

      return;
    }

    setIsClosing(true);
    void run(cancel);
  }, [cancel, leaveEditing, phase, reset, run]);

  const title = isEmail ? (
    <FormattedMessage
      defaultMessage="Email"
      id="profile_email_title"
      description="Email field label"
    />
  ) : (
    <FormattedMessage
      defaultMessage="Phone Number"
      id="profile_phone_title"
      description="Phone field label"
    />
  );

  const changeAction = isEmail ? (
    <FormattedMessage
      defaultMessage="Change Email"
      id="profile_change_email"
      description="Change email action"
    />
  ) : (
    <FormattedMessage
      defaultMessage="Change Phone Number"
      id="profile_change_phone"
      description="Change phone action"
    />
  );

  if (!isEditing) {
    return (
      <div className={s.section} data-test-id={`contact-editor__${identifierType}`}>
        <div className={s.field}>
          <TextField title={title} value={currentValue} disabled={true} />
          <ChangeButton disabled={locked} onClick={beginEditing}>
            {changeAction}
          </ChangeButton>
        </div>
      </div>
    );
  }

  const isCodeStep = phase === IDENTIFIER_UPDATE_PHASES.AWAITING_CODE;
  const errorText = error ? getAuthErrorText(intl, error) : undefined;
  const entryError = validationError ?? (isCodeStep ? undefined : errorText);

  if (phase === IDENTIFIER_UPDATE_PHASES.FAILED) {
    return (
      <div className={s.section} data-test-id={`contact-editor__${identifierType}-failed`}>
        <p>{errorText}</p>
        <Button
          onClick={() => {
            reset();
            leaveEditing();
          }}
          type="button"
        >
          <FormattedMessage
            defaultMessage="Start over"
            id="profile_change_start_over"
            description="Start over button title"
          />
        </Button>
      </div>
    );
  }

  return (
    <div className={s.section} data-test-id={`contact-editor__${identifierType}`}>
      <form onSubmit={handleSubmit} noValidate={true}>
        <div className={s.field}>
          {isEmail ? (
            <TextField
              type="email"
              value={value}
              title={title}
              description={
                isCodeStep ? undefined : (
                  <FormattedMessage
                    defaultMessage="We will send a confirmation code to this address."
                    id="profile_email_change_description"
                    description="Email change note"
                  />
                )
              }
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setValue(e.target.value);
                setValidationError(undefined);
              }}
              placeholder={intl.formatMessage({
                defaultMessage: 'Email',
                id: 'profile_email_placeholder',
                description: 'Email field placeholder',
              })}
              disabled={isCodeStep}
              error={entryError}
              autoFocus={true}
            />
          ) : (
            <PhoneField
              autoFocus={true}
              value={value}
              title={title}
              description={
                isCodeStep ? undefined : (
                  <FormattedMessage
                    defaultMessage="We will send a confirmation code by SMS to this number."
                    id="profile_phone_change_description"
                    description="Phone change note"
                  />
                )
              }
              countryCode={phoneCode}
              onChange={(next: string) => {
                setValue(next);
                setValidationError(undefined);
              }}
              items={PHONE_CODES}
              placeholder={intl.formatMessage({
                defaultMessage: 'Phone number',
                id: 'pTNKg/',
                description: 'Phone field placeholder',
              })}
              onCodeChange={code => setPhoneCode(code.value)}
              disabled={isCodeStep}
              error={entryError}
            />
          )}
          {isCodeStep && (
            <ChangeButton disabled={busy} onClick={handleEditValue}>
              {changeAction}
            </ChangeButton>
          )}
        </div>
        {phase === IDENTIFIER_UPDATE_PHASES.IDLE && (
          <div className={s.actions}>
            <Button isDisabled={!value || busy} type="submit">
              <FormattedMessage
                defaultMessage="Continue"
                id="global_continue"
                description="Continue button title"
              />
            </Button>
            <LinkButton disabled={busy} onClick={handleCancel}>
              <FormattedMessage
                defaultMessage="Cancel"
                id="profile_change_cancel"
                description="Cancel change action"
              />
            </LinkButton>
          </div>
        )}
        {phase === IDENTIFIER_UPDATE_PHASES.SELECTING_TRANSPORT && (
          <div className={s.actions}>
            {transports?.allowedTransports.map(transport => (
              <Button key={transport} onClick={() => run(() => send(transport))} type="button">
                {transport}
              </Button>
            ))}
          </div>
        )}
      </form>
      {isCodeStep && (
        <>
          <CodeForm
            description={
              <FormattedMessage
                defaultMessage="Enter the code we sent to {value}."
                id="profile_change_code_description"
                description="Identifier change code note"
                values={{ value: <b>{pendingValueRef.current}</b> }}
              />
            }
            disabled={busy || isClosing}
            error={errorText}
            attemptsLeft={attemptsLeft}
            resendAvailableIn={resendAvailableIn}
            onSubmit={code => run(() => submitCode(code))}
            onResend={() => run(() => resend())}
          />
          <p className={s.cancel}>
            <LinkButton disabled={busy} onClick={handleCancel}>
              <FormattedMessage
                defaultMessage="Cancel"
                id="profile_change_cancel"
                description="Cancel change action"
              />
            </LinkButton>
          </p>
        </>
      )}
    </div>
  );
};

export { IdentifierSection };
